<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  tags: {
    type: Array,
    default: () => [],
  },
  selectedIndex: {
    type: Number,
    default: 0,
  },
  position: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['select', 'hover', 'close']);

const menuRef = ref(null);

const hasVisibleTags = computed(() => props.show && props.tags.length > 0);

const positionStyle = computed(() => {
  if (props.position) {
    let left = props.position.x;
    let top = props.position.y + 24;

    // Boundary check
    if (menuRef.value) {
      const rect = menuRef.value.getBoundingClientRect();
      if (left + rect.width > window.innerWidth) {
        left = window.innerWidth - rect.width - 20;
      }
      if (top + rect.height > window.innerHeight) {
        top = props.position.y - rect.height - 10;
      }
    }

    return {
      left: `${left}px`,
      top: `${top}px`,
      position: 'fixed',
    };
  }
  return {};
});

const handleSelect = (tag) => {
  emit('select', tag);
};

const handleHover = (index) => {
  emit('hover', index);
};
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 z-40" @click="emit('close')"></div>
  </Teleport>

  <Teleport to="body" :disabled="!position">
    <Transition
      enter-active-class="tag-menu-enter"
      enter-from-class="tag-menu-enter-from"
      enter-to-class="tag-menu-enter-to"
      leave-active-class="tag-menu-leave"
      leave-from-class="tag-menu-leave-from"
      leave-to-class="tag-menu-leave-to"
    >
      <div
        v-if="hasVisibleTags"
        ref="menuRef"
        class="tag-menu mb-2 w-52 bg-white border border-neutral-200/80 rounded-lg shadow-xl shadow-black/8 z-50 overflow-hidden"
        :class="position ? 'fixed' : 'absolute bottom-full left-4'"
        :style="positionStyle"
      >
        <!-- Menu header -->
        <div class="px-3 pt-2.5 pb-1.5 border-b border-neutral-100">
          <span class="text-[11px] font-medium text-neutral-400 uppercase tracking-wider">{{
            $t('editor.insertTag') || 'Insert tag'
          }}</span>
        </div>
        <div class="p-1.5 max-h-[220px] overflow-y-auto minimal-scrollbar">
          <div
            v-for="(tag, index) in tags"
            :key="tag.id"
            class="tag-item flex items-center gap-2.5 px-2.5 py-2 cursor-pointer rounded-md transition-all duration-100"
            :class="{
              'bg-neutral-100 text-neutral-900': index === selectedIndex,
              'text-neutral-600 hover:bg-neutral-50': index !== selectedIndex,
            }"
            @click="handleSelect(tag)"
            @mouseenter="handleHover(index)"
          >
            <span
              class="w-2 h-2 rounded-full ring-2 ring-offset-1 ring-offset-white flex-shrink-0"
              :class="{
                'bg-blue-500 ring-blue-500/20': tag.color === 'blue',
                'bg-violet-500 ring-violet-500/20': tag.color === 'purple',
                'bg-emerald-500 ring-emerald-500/20': tag.color === 'green',
                'bg-amber-500 ring-amber-500/20': tag.color === 'orange',
                'bg-pink-500 ring-pink-500/20': tag.color === 'pink',
                'bg-rose-500 ring-rose-500/20': tag.color === 'red',
                'bg-indigo-500 ring-indigo-500/20': tag.color === 'indigo',
                'bg-teal-500 ring-teal-500/20': tag.color === 'teal',
              }"
            ></span>
            <span class="text-[13px] font-medium flex-1">{{ tag.label }}</span>
            <kbd
              v-if="index === selectedIndex"
              class="text-[10px] px-1.5 py-0.5 bg-neutral-200/60 text-neutral-500 rounded font-mono"
              >↵</kbd
            >
          </div>
        </div>
        <!-- Keyboard hints -->
        <div
          class="px-3 py-2 border-t border-neutral-100 bg-neutral-50/50 flex items-center gap-3 text-[10px] text-neutral-400"
        >
          <span class="flex items-center gap-1">
            <kbd class="px-1 py-0.5 bg-white border border-neutral-200 rounded text-[9px] font-mono"
              >↑</kbd
            >
            <kbd class="px-1 py-0.5 bg-white border border-neutral-200 rounded text-[9px] font-mono"
              >↓</kbd
            >
            navigate
          </span>
          <span class="flex items-center gap-1">
            <kbd class="px-1 py-0.5 bg-white border border-neutral-200 rounded text-[9px] font-mono"
              >esc</kbd
            >
            close
          </span>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.tag-menu {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.tag-menu-enter {
  transition: all 0.15s cubic-bezier(0.32, 0.72, 0, 1);
}

.tag-menu-enter-from {
  opacity: 0;
  transform: translateY(4px) scale(0.98);
}

.tag-menu-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.tag-menu-leave {
  transition: all 0.1s cubic-bezier(0.32, 0.72, 0, 1);
}

.tag-menu-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.tag-menu-leave-to {
  opacity: 0;
  transform: translateY(2px) scale(0.98);
}

.tag-item {
  will-change: background-color;
}

.minimal-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.minimal-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.minimal-scrollbar::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
  transition: background 0.2s ease;
}

.minimal-scrollbar:hover::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
}
</style>
